import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { FlexProps } from 'styled-system';
import {
  alignItems,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  color,
  display,
  flex,
  space,
  themeGet,
  width,
} from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import { boxSizing, cursor, textDecoration } from '../../theme/system-utilities';
import type { WithTheme } from '../../theme/theme';
import type { TrackingEvent } from '../../tracking/getTrackerProps';
import { Link } from '../Link';
import { CaptionRegularCR1 } from '../Typography';
import Image from './SuggestionImage';
import SuggestionsListItemName from './SuggestionsListItemName';
import type { SuggestionItem } from './types';
import { formatCountDisplay } from './utils/search';
import { trackSuggestionClick } from './utils/tracking';

const UnstyledSuggestionsListItemContainer = styled.li<any>`
  ${display};
  ${width};
  ${boxSizing};
`;

const SuggestionsListItemContainer = (
  props: ComponentProps<typeof UnstyledSuggestionsListItemContainer>
) => (
  <UnstyledSuggestionsListItemContainer
    display="block"
    width={1}
    boxSizing="border-box"
    {...props}
  />
);

const suggestionsListItemLinkHighlightedCSS = ({ theme }: WithTheme) => css`
  color: ${themeGet('colors.sys.neutral.text.hover')({
    theme,
  })};
  background-color: ${themeGet('colors.sys.neutral.background.hover')({
    theme,
  })};
  border-color: ${themeGet('colors.sys.neutral.border.hover')({ theme })};
`;

const UnstyledSuggestionsListItemLink = styled(Link)<any>`
  ${color};
  ${borderWidth};
  ${borderStyle};
  ${borderColor};
  ${borderRadius};
  ${display};
  ${alignItems};
  ${width};
  ${space};
  ${boxSizing};
  ${cursor};

  :hover,
  :focus,
  :active {
    ${color};
    ${textDecoration};
  }

  ${({ highlighted }) => highlighted && suggestionsListItemLinkHighlightedCSS};

  :focus,
  :active {
    outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
    border-color: transparent;
  }
`;

const SuggestionsListItemLink = (props: ComponentProps<typeof UnstyledSuggestionsListItemLink>) => (
  <UnstyledSuggestionsListItemLink
    display="flex"
    alignItems="center"
    p="sp_8"
    boxSizing="border-box"
    cursor="pointer"
    width={1}
    color="sys.neutral.text.default"
    borderWidth="1px"
    borderStyle="solid"
    borderColor="transparent"
    borderRadius={[null, 'medium']}
    textDecoration="none"
    {...props}
  />
);

const UnstyledSuggestionsListItemCount = styled(CaptionRegularCR1)<FlexProps>`
  ${flex};
`;

const SuggestionsListItemCount = (
  props: ComponentProps<typeof UnstyledSuggestionsListItemCount>
) => (
  <UnstyledSuggestionsListItemCount
    as="span"
    flex="none"
    ml="sp_8"
    whiteSpace="nowrap"
    {...props}
  />
);

interface SuggestionsListItemProps {
  suggestionsItem: SuggestionItem;
  highlighted: boolean;
  inputValue: string;
  isMobile: boolean;
  listIndex: number;
  listItems: string[];
  exitSuggestions: (shouldClearInput: boolean) => void;
  shopName?: string;
}

const useAriaLabel = (suggestionsItem: SuggestionItem, displayCount?: string | number) => {
  const { t } = useLocalization();
  if (!suggestionsItem.count || !displayCount) {
    return suggestionsItem.name;
  }

  return `${suggestionsItem.name}, ${displayCount} ${t('h24_products')}`;
};

const SuggestionsListItem = ({
  suggestionsItem,
  highlighted,
  inputValue,
  isMobile,
  listIndex,
  listItems,
  exitSuggestions,
  shopName,
  ...props
}: SuggestionsListItemProps) => {
  const displayCount = formatCountDisplay(suggestionsItem.count);
  const ariaLabel = useAriaLabel(suggestionsItem, displayCount);
  const handleLinkClick = (event: TrackingEvent) => {
    // We want to preserve link behaviour
    // and not delegate it to Downshift,
    // that way people can click on a link
    // to open in new tabs, etc
    event.stopPropagation();
    trackSuggestionClick({
      inputValue,
      label: suggestionsItem.name,
      position: listIndex,
      length: listItems.length,
      shopName,
    });
  };

  return (
    <SuggestionsListItemContainer
      data-testid="suggestions-list-item"
      highlighted={highlighted}
      data-cnstrc-item-section="Search Suggestions"
      {...props}
    >
      <SuggestionsListItemLink
        highlighted={highlighted}
        href={suggestionsItem.urlEncoded}
        onClick={handleLinkClick}
        data-testid="suggestions-list-item-link"
        aria-label={ariaLabel}
      >
        <Image src={suggestionsItem.image} alt="" data-testid="suggestions-list-item-image" />
        <SuggestionsListItemName suggestionsItem={suggestionsItem} inputValue={inputValue} />
        {displayCount && (
          <SuggestionsListItemCount data-testid="suggestions-list-item-count" aria-hidden={true}>
            {displayCount}
          </SuggestionsListItemCount>
        )}
      </SuggestionsListItemLink>
    </SuggestionsListItemContainer>
  );
};

export { SuggestionsListItemContainer, SuggestionsListItemLink };

export default SuggestionsListItem;
